import { useState, useRef, useEffect } from "react";
import { Search, Bell, ChevronDown, Menu, Sun, Moon, Check, AlertCircle } from "lucide-react";
import { LogOut, User, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { API_BASE_URL } from "../config";

function Header({ onMenuClick, title = "Dashboard", subtitle }) {

    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();

    // Notifications state
    const [notifOpen, setNotifOpen] = useState(false);
    const notifDropdownRef = useRef(null);
    const [notifications, setNotifications] = useState([]);
    const [readIds, setReadIds] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("sb_read_notif_ids") || "[]");
        } catch {
            return [];
        }
    });
    const lastNotifIdsRef = useRef(new Set());
    const isFirstLoadRef = useRef(true);

    // Audio chime generator using Web Audio API
    const playNotificationSound = () => {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!AudioContext) return;
            const ctx = new AudioContext();
            const now = ctx.currentTime;

            // First chime tone
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.type = "sine";
            osc1.frequency.setValueAtTime(880, now); // A5
            gain1.gain.setValueAtTime(0.18, now);
            gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
            osc1.connect(gain1);
            gain1.connect(ctx.destination);
            osc1.start(now);
            osc1.stop(now + 0.35);

            // Second higher chime tone
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.type = "sine";
            osc2.frequency.setValueAtTime(1174.66, now + 0.12); // D6
            gain2.gain.setValueAtTime(0.22, now + 0.12);
            gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.65);
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.start(now + 0.12);
            osc2.stop(now + 0.65);
        } catch (e) {
            console.warn("Notification sound could not be played:", e);
        }
    };

    // Fetch notifications
    const fetchNotifications = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/notifications`);
            const data = await res.json();
            if (data && data.success && Array.isArray(data.notifications)) {
                const fetched = data.notifications;
                setNotifications(fetched);

                // Detect new incoming alerts to play sound
                if (!isFirstLoadRef.current) {
                    const hasNewAlert = fetched.some(
                        (n) => !lastNotifIdsRef.current.has(n.notification_id_PK)
                    );
                    if (hasNewAlert) {
                        playNotificationSound();
                    }
                } else {
                    isFirstLoadRef.current = false;
                }

                lastNotifIdsRef.current = new Set(fetched.map((n) => n.notification_id_PK));
            }
        } catch (err) {
            console.error("Header notification fetch error:", err);
        }
    };

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 15000); // Poll every 15s
        return () => clearInterval(interval);
    }, []);

    // Outside click listener for notification and profile dropdowns
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (notifDropdownRef.current && !notifDropdownRef.current.contains(e.target)) {
                setNotifOpen(false);
            }
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Calculate unread count
    const unreadCount = notifications.filter(
        (n) => !readIds.includes(n.notification_id_PK)
    ).length;

    // Mark single notification as read
    const handleNotificationClick = (notif) => {
        if (!readIds.includes(notif.notification_id_PK)) {
            const updated = [...readIds, notif.notification_id_PK];
            setReadIds(updated);
            localStorage.setItem("sb_read_notif_ids", JSON.stringify(updated));
        }
        setNotifOpen(false);
        navigate("/notifications");
    };

    // Mark all as read
    const handleMarkAllAsRead = (e) => {
        e.stopPropagation();
        const allIds = notifications.map((n) => n.notification_id_PK);
        setReadIds(allIds);
        localStorage.setItem("sb_read_notif_ids", JSON.stringify(allIds));
    };

    return (
        <header className="bg-white dark:bg-slate-800 sticky top-0 z-40 h-20 flex items-center justify-between px-4 sm:px-8 border-b border-slate-200 dark:border-slate-700 shadow-sm transition-all duration-300">

            {/* Left */}
            <div className="flex items-center gap-2 sm:gap-4">
                <button onClick={onMenuClick} className="lg:hidden p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors">
                    <Menu size={24} />
                </button>
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100 tracking-tight">
                        {title}
                    </h1>
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1 hidden sm:block">
                        {subtitle || (
                            <>
                                Welcome back, <span className="text-blue-600 dark:text-blue-400 font-medium">Admin </span>
                            </>
                        )}
                    </p>
                </div>
            </div>

            {/* Right */}
            <div className="flex items-center gap-3 sm:gap-6">

                {/* Mobile Search Icon */}
                <button className="md:hidden relative bg-white dark:bg-slate-800 p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-all shadow-sm text-slate-500 dark:text-slate-400">
                    <Search className="w-5 h-5" />
                </button>

                {/* Theme Toggle */}
                <button
                    onClick={toggleTheme}
                    className="relative bg-white dark:bg-slate-800 p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-all shadow-sm text-slate-500 dark:text-slate-400"
                >
                    {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>

                {/* Notification Bell with Dropdown */}
                <div className="relative" ref={notifDropdownRef}>
                    <button
                        onClick={() => setNotifOpen(!notifOpen)}
                        className={`relative p-2.5 rounded-xl border transition-all shadow-sm group ${notifOpen
                                ? "bg-blue-50 border-blue-300 dark:bg-slate-700 dark:border-blue-500"
                                : "bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 border-slate-200 dark:border-slate-700"
                            }`}
                        title="Notifications"
                    >
                        <Bell
                            className={`w-5 h-5 transition-colors ${unreadCount > 0
                                    ? "text-rose-600 dark:text-rose-400 animate-[wiggle_1s_ease-in-out_infinite]"
                                    : "text-slate-500 group-hover:text-blue-600"
                                }`}
                        />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-rose-600 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center border-2 border-white dark:border-slate-800 shadow-sm animate-pulse">
                                {unreadCount > 9 ? "9+" : unreadCount}
                            </span>
                        )}
                    </button>

                    {/* Notification Dropdown Menu */}
                    {notifOpen && (
                        <div className="absolute right-0 mt-3 w-80 sm:w-96 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                            {/* Header */}
                            <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-slate-50/70 dark:bg-slate-900/40">
                                <div className="flex items-center gap-2">
                                    <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm">
                                        Notifications
                                    </h3>
                                    {unreadCount > 0 && (
                                        <span className="bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300 text-xs font-semibold px-2 py-0.5 rounded-full">
                                            {unreadCount} new
                                        </span>
                                    )}
                                </div>
                                {unreadCount > 0 && (
                                    <button
                                        onClick={handleMarkAllAsRead}
                                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-medium"
                                    >
                                        <Check size={14} /> Mark all read
                                    </button>
                                )}
                            </div>

                            {/* Notifications List */}
                            <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700/60">
                                {notifications.length === 0 ? (
                                    <div className="p-8 text-center text-slate-500 dark:text-slate-400 text-xs">
                                        No notifications found.
                                    </div>
                                ) : (
                                    notifications.slice(0, 8).map((n) => {
                                        const isUnread = !readIds.includes(n.notification_id_PK);
                                        const isAlert = n.title && n.title.toLowerCase().includes("alert");
                                        return (
                                            <div
                                                key={n.notification_id_PK}
                                                onClick={() => handleNotificationClick(n)}
                                                className={`p-3.5 hover:bg-slate-50 dark:hover:bg-slate-700/60 cursor-pointer transition flex items-start gap-3 ${isUnread
                                                        ? "bg-blue-50/50 dark:bg-blue-950/20"
                                                        : ""
                                                    }`}
                                            >
                                                <div
                                                    className={`w-8 h-8 rounded-xl shrink-0 flex items-center justify-center mt-0.5 ${isAlert
                                                            ? "bg-rose-100 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400"
                                                            : "bg-blue-100 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400"
                                                        }`}
                                                >
                                                    {isAlert ? <AlertCircle size={16} /> : <Bell size={16} />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">
                                                            {n.title}
                                                        </p>
                                                        {isUnread && (
                                                            <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0"></span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 line-clamp-2">
                                                        {n.description}
                                                    </p>
                                                    <span className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 block">
                                                        {n.time || (n.created_at ? new Date(n.created_at).toLocaleString("en-IN") : "")}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Footer */}
                            <div className="p-2.5 border-t border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30 text-center">
                                <button
                                    onClick={() => {
                                        setNotifOpen(false);
                                        navigate("/notifications");
                                    }}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold py-1 px-3 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/40 transition"
                                >
                                    View all notifications →
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="w-px h-8 bg-slate-200 dark:bg-slate-700 mx-2"></div>

                {/* Profile */}
                <div className="relative" ref={dropdownRef}>
                    {/* Profile */}
                    <div
                        onClick={() => setOpen(!open)}
                        className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 p-2 rounded-xl transition-all border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                    >
                        <div className="relative">
                            <img
                                src="https://i.pravatar.cc/150?u=admin"
                                alt="Admin"
                                className="w-10 h-10 rounded-xl object-cover border border-slate-200 shadow-sm"
                            />
                            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></div>
                        </div>

                        <div className="hidden sm:block">
                            <h4 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">
                                Super Admin
                            </h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                admin@socialbazar.co
                            </p>
                        </div>

                        <ChevronDown
                            className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""
                                }`}
                        />
                    </div>

                    {/* Dropdown */}
                    {open && (
                        <div className="absolute right-0 mt-3 w-56 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50">

                            <button
                                onClick={() => {
                                    setOpen(false);
                                    navigate("/profile");
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700 dark:text-slate-200 transition"
                            >
                                <User size={18} />
                                Profile
                            </button>

                            <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700 dark:text-slate-200 transition">
                                <Settings size={18} />
                                Settings
                            </button>

                            <hr className="dark:border-slate-700" />
                            <button
                                onClick={() => {
                                    // Agar token store kiya hai to remove karo
                                    localStorage.removeItem("token");
                                    sessionStorage.clear();

                                    // Login page par redirect
                                    navigate("/");
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 transition"
                            >
                                <LogOut size={18} />
                                Logout
                            </button>

                        </div>
                    )}
                </div>

            </div>

        </header>
    );
}

export default Header;